<script setup lang="ts">
/**
 * Fleck field (#241) — a scatter of tiny red-and-gold specks, the flecks of gilt
 * and madder that ride on the brand's dark banner. Part of the material layer;
 * ported from the styleguide reference. The banner itself (#244) is a separate
 * ticket — this is the reusable speck primitive it will sit on.
 *
 * A `<canvas>` rather than DOM nodes: a hundred-plus sub-pixel dots are far cheaper
 * to paint once than to lay out as elements. Colours are read from the live
 * --accent / --gold tokens at draw time, and the count defaults to the --flecks
 * token (the styleguide tunes density per theme), so the field re-tints and
 * re-densifies when the theme flips. Redrawn on resize and on a data-theme change.
 *
 * Deterministic-by-default: dot positions are random, but the field is decorative
 * (`aria-hidden`) so the randomness is purely cosmetic.
 */
import { ref, onMounted } from 'vue'
import { useResizeObserver, useMutationObserver } from '@vueuse/core'

const props = withDefaults(defineProps<{ count?: number }>(), { count: 0 })

const canvas = ref<HTMLCanvasElement | null>(null)

function draw(): void {
  const el = canvas.value
  if (!el) return
  const ctx = el.getContext('2d')
  if (!ctx) return

  const rect = el.getBoundingClientRect()
  const dpr = window.devicePixelRatio || 1
  el.width = Math.max(1, Math.round(rect.width * dpr))
  el.height = Math.max(1, Math.round(rect.height * dpr))
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, rect.width, rect.height)

  const cs = window.getComputedStyle(el)
  const gold = cs.getPropertyValue('--gold').trim() || '#b8924e'
  const accent = cs.getPropertyValue('--accent').trim() || '#97352e'
  const n = props.count || parseInt(cs.getPropertyValue('--flecks'), 10) || 130

  for (let i = 0; i < n; i++) {
    const x = Math.random() * rect.width
    const y = Math.random() * rect.height
    const r = 0.3 + Math.random() * 1.4 // 0.3–1.7px specks
    ctx.globalAlpha = 0.12 + Math.random() * 0.55 // 0.12–0.67
    ctx.fillStyle = Math.random() < 0.6 ? gold : accent // 60% gilt, 40% madder
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.globalAlpha = 1
}

onMounted(draw)
useResizeObserver(canvas, () => draw())
useMutationObserver(document.documentElement, () => draw(), {
  attributes: true,
  attributeFilter: ['data-theme'],
})

defineExpose({ draw })
</script>

<template>
  <canvas ref="canvas" class="fleck-field" aria-hidden="true"></canvas>
</template>

<style scoped>
.fleck-field {
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
</style>
